import React, { useState, useEffect, useCallback } from 'react'
import * as siteMaster from '~/data/actions/siteActions'
import { useDispatch, useSelector } from 'react-redux'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import CheckboxTile from '~/Common-Objects/UI/Forms/CheckboxTile'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import NewFeatureFeedback from '~/Common-Objects/NewFeatureFeedback'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import TierLimitHolder from '~/Common-Objects/UI/Cards/TierLimitHolder'
import UpsellModal from '~/Common-Objects/UpsellModal'
import i18n from '~/i18n'
import axios from '~/data/http/axios'
import tierPermissions from '~/Classes/tierPermissions'
import DrawerAdCampaign from './adCampaign'
import PostHogFunctions from '~/Classes/posthog'
import useTierAllowed from '~/Common-Objects/hooks/useTierAllowed'

const DrawerAddCampaignStep1 = (props) => {
    const dispatch = useDispatch()
    const user = useSelector((store) => store.user)
    const accountMaster = useSelector((store) => store.accountMaster)
    const site = useSelector((store) => store.siteMaster)

    const [selectedCampaignType, setSelectedCampaignType] = useState(props.selectSms || props.campaignType || 1)
    const [campaignName, setCampaignName] = useState('')
    const [creatingCampaign, setCreatingCampaign] = useState(false)
    const [campaignNameErr, setCampaignNameErr] = useState('')
    const [interestedInCampaign, setInterestedInCampaign] = useState('')
    const [abSplitPermissions, setAbSplitPermissions] = useState(null)
    const [socialPermissions, setSocialPermissions] = useState(null)
    const [upselling, setUpselling] = useState()
    const [smsPlans, setSmsPlans] = useState([])
    const socialTierCheck = useTierAllowed('socialCampaigns')
    const abTierCheck = useTierAllowed('abSplitTests')

    useEffect(() => {
        axios.get('/accountMaster/tier/abSplitTests').then((res) => {
            setAbSplitPermissions(res.data)
        })
        axios.get('/accountMaster/tier/socialCampaigns').then((res) => {
            setSocialPermissions(res.data)
        })
        axios.get('/accountMaster/plan?type=2').then((res) => setSmsPlans(res.data.Plans))

        if (props.selectSms || props.campaignType === 6) {
            clickSms()
        }
    }, [])

    const selectCampaignType = useCallback(
        (campaignType) => {
            if (campaignType == 30 && !socialTierCheck.tierAllowed && socialTierCheck.requiredTier) {
                setUpselling('socialCampaigns')
                upsell('socialCampaigns', socialTierCheck.requiredTier?.name)
                return
            }
            if (campaignType == 10 && !abTierCheck.tierAllowed && abTierCheck.requiredTier) {
                setUpselling('abSplitTests')
                upsell('abSplit', abTierCheck.requiredTier?.name)
                return
            }
            setSelectedCampaignType(campaignType)
        },
        [socialTierCheck]
    )

    const closeDrawer = useCallback(() => {
        dispatch(siteMaster.alterSiteDrawer(false, true, 'left', null))
    }, [dispatch])

    const selectAdCampaign = useCallback(() => {
        // Placeholder for future logic
    }, [])

    const upsell = (feature, tier, plans) => {
        const modal = (
            <UpsellModal
                inline={true}
                plans={plans}
                feature={feature}
                requiredTier={tier}
                onUpgrade={() => {
                    dispatch(siteMaster.alterFullpage(null))
                }}
            />
        )
        dispatch(siteMaster.alterFullpage(modal))
    }

    const clickSms = useCallback(() => {
        if (accountMaster.accountMaster.country === 'United Kingdom' || accountMaster.accountMaster.options?.allowSms) {
            selectCampaignType(6)
        } else {
            upsell('sms', null, smsPlans)
            selectCampaignType(1)
        }
    }, [accountMaster, selectCampaignType])

    const startCampaign = useCallback(async () => {
        if (!selectedCampaignType) return

        let name = campaignName
        if (typeof name === 'string') name = name.trim()
        if (name === null || name === undefined || name.length < 4) {
            setCampaignNameErr('Please enter a longer name')
            setCreatingCampaign(false)
            setCampaignName(name)
            return
        }

        setCampaignNameErr('')
        setCreatingCampaign(true)

        let posthogtype = 'email'
        let theURL = '/cp/campaigns/add/standard/'
        switch (selectedCampaignType) {
            case 1:
                theURL = '/cp/campaigns/add/standard/:campaignId/1'
                posthogtype = 'email'
                break
            case 10:
                theURL = '/cp/campaigns/add/ab/:campaignId'
                posthogtype = 'ab'
                break
            case 3:
                theURL = '/cp/campaigns/add/automation/:campaignId'
                posthogtype = 'automation'
                break
            case 6:
                theURL = '/cp/campaigns/add/sms/:campaignId'
                posthogtype = 'sms'
                break
            case 20:
                theURL = '/cp/campaigns/add/directMail/:campaignId'
                posthogtype = 'directmail'
                break
            case 30:
                theURL = '/cp/campaigns/add/social/:campaignId'
                posthogtype = 'social'
                break
            default:
                theURL = '/cp/campaigns/add/standard/:campaignId/1'
                break
        }
        let options = {}
        if (user.userData.timeZone) {
            options.timezone = user.userData.timeZone
        }
        try {
            let res = await axios.post('/campaign', {
                campaignName: name,
                type: selectedCampaignType,
                startDate: props.startDate || new Date(),
                options,
                Groups: props.Groups
            })
            theURL = theURL.replace(':campaignId', res.data.Campaign.id)

            PostHogFunctions.capture('campaign_created', {
                type: posthogtype
            })
            if (posthogtype) {
                PostHogFunctions.capture(posthogtype + '_campaign_created')
            }

            dispatch(siteMaster.alterSiteDrawer(false, true, 'left', null))
            props.history.push(theURL)
        } catch (err) {
            let message = i18n.t('campaigns:add.nameError')
            if (err.data && err.data.error && typeof err.data.error === 'string') message = err.data.error
            setCampaignNameErr(message)
            setCreatingCampaign(false)
        }
    }, [selectedCampaignType, campaignName, user, accountMaster, props, dispatch])

    useEffect(() => {
        if (upselling === 'socialCampaigns' && socialTierCheck.tierAllowed) {
            setUpselling(null)
            selectCampaignType(30)
        } else if (upselling === 'abSplitTests' && abTierCheck.tierAllowed) {
            setUpselling(null)
            selectCampaignType(10)
        }
    }, [socialTierCheck.requiredTier, socialTierCheck.tierAllowed])

    if (selectedCampaignType == 40) {
        return (
            <DrawerAdCampaign
                history={props.history}
                exit={() => {
                    setSelectedCampaignType(1)
                }}
            />
        )
    }

    let abPermissions = tierPermissions('abSplitTests', accountMaster)
    let socialPerms = tierPermissions('socialCampaigns', accountMaster)
    if (creatingCampaign || !abSplitPermissions || !socialPermissions) return <LoadingIcons iconType='2' />

    let allowed = true
    if (selectedCampaignType == 10 && abPermissions.allowed == false) {
        allowed = false
    }
    if (selectedCampaignType == 30 && socialPerms.allowed == false) {
        allowed = false
    }

    if (interestedInCampaign !== '') {
        let featureSubType = ''
        switch (interestedInCampaign) {
            case 'sms': {
                featureSubType = 'SMS Campaigns'
                break
            }
        }
        return (
            <NewFeatureFeedback
                featureType='campaign'
                featureSubType={featureSubType}
                closeDrawer={() => {
                    setInterestedInCampaign('')
                }}
            />
        )
    }
    return (
        <div className='campaign-type-selector'>
            <div className='campaign-type-selector__content'>
                <div className='exit' onClick={closeDrawer}>
                    <span className='icon-cross2'></span>
                </div>
                <h4 className='mar-b10'>{i18n.t('campaigns:main.createCampaign')}</h4>
                <div className='mar-b25'>{i18n.t('campaigns:add.header')}</div>

                <TextInput
                    outerStyle={{ marginBottom: 24 }}
                    label={i18n.t('Name')}
                    value={campaignName}
                    onChange={(ev) => {
                        setCampaignName(ev.target.value)
                    }}
                    error={campaignNameErr}
                    onEnterPress={startCampaign}
                    focus
                    className='cyt-new-campaign-text-input'
                />

                <div className='textInputWrapper' style={{ marginBottom: 8 }}>
                    <div className='txtInput_label'>Type</div>
                </div>

                <div className='row'>
                    <div className='col-xs-12 mar-b16'>
                        <CheckboxTile
                            name='selectedCampaignType'
                            value='1'
                            checked={selectedCampaignType == 1}
                            onChange={() => selectCampaignType(1)}
                            outerStyle={{ height: '100%' }}
                            header={i18n.t('campaigns:add.plain')}
                            subheader={i18n.t('campaigns:add.plainText')}
                            icon='envelope'
                            postHogTag="add-campaign-type-email"
                        />
                    </div>
                    <div className='col-xs-12 mar-b16'>
                        <CheckboxTile
                            name='selectedCampaignType'
                            value='10'
                            checked={selectedCampaignType == 10}
                            onChange={() => selectCampaignType(10)}
                            outerStyle={{ height: '100%' }}
                            requiredTier={!abTierCheck.tierAllowed && abTierCheck.requiredTier?.name}
                            data-ph={
                                !abTierCheck.tierAllowed && abTierCheck.requiredTier?.name && 'absplit_campaign_upsell'
                            }
                            header={i18n.t('campaigns:add.split')}
                            subheader={i18n.t('campaigns:add.splitText')}
                            icon='split'
                            postHogTag="add-campaign-type-ab-split"
                        />
                    </div>

                    {site.siteId === 1 && (
                        <div className='col-xs-12 mar-b16'>
                            <CheckboxTile
                                name='selectedCampaignType'
                                value='30'
                                checked={selectedCampaignType == 30}
                                onChange={() => selectCampaignType(30)}
                                outerStyle={{ height: '100%' }}
                                requiredTier={!socialTierCheck.tierAllowed && socialTierCheck.requiredTier?.name}
                                data-ph={
                                    !socialTierCheck.tierAllowed &&
                                    socialTierCheck.requiredTier?.name &&
                                    'social_campaign_upsell'
                                }
                                header={i18n.t('campaigns:add.social')}
                                subheader={i18n.t('campaigns:add.socialText')}
                                icon='share'
                                postHogTag="add-campaign-type-social"
                            />
                        </div>
                    )}

                    {!!accountMaster.accountMaster.beta && accountMaster.accountMaster.beta.ads && (
                        <div className='col-xs-12 mar-b16'>
                            <CheckboxTile
                                name='selectedCampaignType'
                                value='40'
                                checked={selectedCampaignType == 40}
                                onChange={() => {
                                    selectCampaignType(40)
                                    selectAdCampaign()
                                }}
                                outerStyle={{ height: '100%' }}
                                header={i18n.t('campaigns:add.adCampaign')}
                                subheader={i18n.t('campaigns:add.adCampaignText')}
                                icon='megaphone'
                                postHogTag="add-campaign-type-ad-campaign"
                            />
                        </div>
                    )}

                    {site.siteId === 1 && (
                        <div className='col-xs-12 mar-b16'>
                            <CheckboxTile
                                name='selectedCampaignType'
                                value='6'
                                checked={selectedCampaignType == 6}
                                onChange={clickSms}
                                outerStyle={{ height: '100%' }}
                                header={i18n.t('campaigns:add.sms')}
                                subheader={i18n.t('campaigns:add.smsText')}
                                icon='sms'
                                postHogTag="add-campaign-type-sms"
                            />
                        </div>
                    )}
                </div>

                {!abSplitPermissions?.allowed && selectedCampaignType == 10 && (
                    <TierLimitHolder type='AB Split Tests' {...abSplitPermissions} />
                )}
            </div>

            <div className='campaign-type-selector__sticky-footer'>
                <div className='campaign-type-selector__sticky-footer__content'>
                    <Button
                        disabled={!allowed}
                        className='button-primary mar-r10 cyt-new-campaign-btn'
                        text={i18n.t('campaigns:add.nameButton')}
                        onClick={startCampaign}
                    />
                    <Button onClick={closeDrawer}>Cancel</Button>
                </div>
            </div>
        </div>
    )
}

export default DrawerAddCampaignStep1
